import { adviseUpdate, type UpdateAdvice } from "@codeshare/session";
import { useEffect, useState } from "react";
import { BUILD_INFO } from "../build-info";
import { appUpdater } from "../pwa/register";
import type { RoomState } from "../session/room-controller";

async function fetchDeployed() {
  const response = await fetch(`${import.meta.env.BASE_URL}version.json`, { cache: "no-store" });
  if (!response.ok) return undefined;
  const info = (await response.json()) as { version?: unknown; builtAt?: unknown };
  return typeof info.version === "string" && typeof info.builtAt === "number"
    ? { version: info.version, builtAt: info.builtAt }
    : undefined;
}

/** Tells the user, when a peer runs a newer build, whether refreshing will update them. */
export function VersionBanner({ room }: { room: RoomState }) {
  const [advice, setAdvice] = useState<UpdateAdvice>({ kind: "none" });
  const builds = room.peers.flatMap((p) =>
    p.app && p.profile ? [{ name: p.profile.name, app: p.app }] : [],
  );
  const key = builds
    .map((b) => `${b.name}@${b.app.version}/${b.app.builtAt}/${b.app.origin}`)
    .join(",");

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` captures the builds that matter.
  useEffect(() => {
    let cancelled = false;
    void adviseUpdate({ ...BUILD_INFO, origin: location.origin }, builds, fetchDeployed).then(
      (next) => {
        if (!cancelled) setAdvice(next);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key]);

  switch (advice.kind) {
    case "none":
      return null;
    case "update-available":
      return (
        <div className="banner" role="status">
          <span>
            {advice.peerName} is on v{advice.peerVersion} (you're on v{advice.localVersion}).
            Refresh to update.
          </span>
          <button
            type="button"
            onClick={() => void appUpdater.checkForUpdate().then(() => appUpdater.applyUpdate())}
          >
            Refresh to update
          </button>
        </div>
      );
    case "not-deployed":
      return (
        <div className="banner" role="status">
          {advice.peerName} is on a newer build (v{advice.peerVersion}) that isn't available here
          yet.
        </div>
      );
    case "other-origin":
      return (
        <div className="banner" role="status">
          {advice.peerName} is using a newer build from {advice.origin}.
        </div>
      );
  }
}
